// User profile helpers — wrap Firestore reads/writes for `users/{uid}`.
// Components and pages should call these rather than touching Firestore
// directly so the schema stays in one place.

import { getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { User } from 'firebase/auth';
import { functions } from '../firebase';
import { userDoc } from './firestore';
import type { UserProfile } from './types';

export interface CreateUserProfileInput {
  uid: string;
  email: string;
  name?: string;
  phone?: string;
  mfaEnrolled: boolean;
}

/**
 * Create a fresh user profile document. Used after signup once the user has
 * enrolled in MFA. Idempotent — safe to call twice (uses `setDoc` with merge).
 */
export async function createUserProfile(input: CreateUserProfileInput): Promise<void> {
  const { uid, email, name, phone, mfaEnrolled } = input;

  // We let Firestore stamp the createdAt server-side rather than trusting the
  // client clock. The UserProfile type uses `number` (ms epoch) on the client;
  // serverTimestamp resolves to a Firestore Timestamp on write and we cast on
  // read. This avoids client-clock drift skewing audit logs.
  await setDoc(
    userDoc(uid),
    {
      id: uid,
      email,
      name: name ?? '',
      phone: phone ?? null,
      orgId: null,
      biometricEnabled: false,
      mfaEnrolled,
      createdAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/**
 * Read the user profile doc. Returns null when the doc does not exist —
 * e.g. a freshly-created auth account that hasn't completed signup.
 */
export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const snap = await getDoc(userDoc(uid));
  if (!snap.exists()) return null;
  // Firestore returns the doc as the SDK's generic shape; we know the schema
  // matches UserProfile except for createdAt, which can be a Timestamp on
  // first read after a serverTimestamp() write.
  const raw = snap.data() as Omit<UserProfile, 'createdAt'> & {
    createdAt: number | { toMillis(): number };
  };
  return {
    ...raw,
    createdAt:
      typeof raw.createdAt === 'number' ? raw.createdAt : raw.createdAt.toMillis(),
  };
}

/**
 * Convenience: derive a CreateUserProfileInput from a Firebase Auth user.
 * Used by the signup flow so callers don't have to hand-pluck fields.
 */
export function userProfileInputFromAuthUser(user: User, mfaEnrolled: boolean): CreateUserProfileInput {
  return {
    uid: user.uid,
    email: user.email ?? '',
    name: user.displayName ?? '',
    phone: user.phoneNumber ?? undefined,
    mfaEnrolled,
  };
}

/**
 * Update the caller's own profile via Cloud Function. The function fans
 * the name out to any member docs so denormalised displayName stays in
 * sync. Pass null to phone to clear it; omit a field to leave unchanged.
 */
export async function updateUserProfile(updates: {
  name?: string;
  phone?: string | null;
}): Promise<void> {
  const callable = httpsCallable(functions, 'updateUserProfile');
  await callable(updates);
}
